/**
 * Preset voices that should be spoken with the expressive v3 model
 * (`eleven_v3`). These voices support inline audio tags like [laughs] /
 * [whispers], get the in-editor tag highlighting, and are capped at 5,000
 * characters per generation.
 *
 * v3 is a *model*, so technically any voice can be used with it — this is a
 * curated allow-list of the voices we expose as tag-capable. To mark more
 * voices, just add their `voice_id` here; the model flag is then applied
 * everywhere automatically (list-voices serves `model: 'eleven_v3'`,
 * the voice picker shows/filters them, and generate-audio routes them to v3).
 */
export const V3_VOICE_IDS = new Set<string>([
  'BIvP0GN1cAtSRTxNHnWS', // Ellen — calm female voice with international accent
  'EkK5I93UQWFDigLMpZcX',
  'UgBBYS2sOqTuMpoF3BR0',
  'RILOU7YmBhvwJGDGjNmP',
  'tnSpp4vdxKPjI9w0GnoV',
  'Bj9UqZbhQsanLzgalpEG',
  'B8gJV1IhpuegLxdpXFOE',
  'exsUS4vynmxd379XN4yO',
  '2zRM7PkgwBPiau2jvVXc',
  '5l5f8iK3YPeGga21rQIX',
  'NOpBlnGInO9m6vDvFkFC',
  // Singaporean voices designed via the Voice Design feature
  '7gXCLoOYk97cdgjFgk7I', // Aishah — social media
  'tD4Um2ziev6ERZlXxKPm', // Wei Ming — corporate narration
  '8WtLxYgvSoySH8pY3vPW', // Priya — e-learning
  'XQRyQfTsmGG2ZVsdugVP', // Daniel Tan — podcast host
  'YuJqB6WbAuZY1z5OCcB4', // Siti — storytelling
  'CSIDIJJN91pzKTwpTKtT', // Rajesh — documentary
  'mLRBCHJgimf6AimxZv9g', // Mei Hui — commercial ad
  'RMluRFOaTCjh4Crc17Am', // Hafiz — social media
  // New-language voices (Persian, Urdu, Indian languages...) — these languages
  // are only supported by the v3 model, so they must always route to eleven_v3.
  'PleK417YVMP2SUWm8Btb', // Amir — Persian (Farsi)
  'NZiuR1C6kVMSWHG27sIM', // Noushin — Persian (Farsi)
  'WwAjIyMBDBNl1dvId9Xe', // Nazy — Persian (Farsi)
  'rNb3hdSf0n4ROIbYC8Bl', // Shahram — Persian (Farsi)
  '9cI5mhBtM4WtQ9Fo6jWQ', // Sara — Urdu
  'aPfeouerZvEVukwmLSP0', // Haseeb — Urdu
  'UT6USLtoAlXHj5k4sOLY', // Reva — Urdu
  'kD4dEWy2fbcyXlge6iHh', // A-Xee — Urdu
  'WiaIVvI1gDL4vT4y7qUU', // Suman — Bengali
  'FDQcYNtvPtQjNlTyU3du', // Sumi — Bengali
  'FhOnCtjmaAIRIS1Dg2bk', // Biju — Bengali
  'q4yUdydpWxqfmkQH7gK7', // Swati — Bengali
  'EMxdghWQV7gqV33j4J3F', // Abhi — Telugu
  '2DRBj9T2XZ7Jmkcm6WCZ', // Sagar — Telugu
  'QKyvRuehpb8zB3cRkzIn', // Seeta — Telugu
  'eh5YJlyB5bmWfcmcEsLN', // Vandana — Telugu
  'RxnH5jCRKb1ez2lcmQC1', // Pind Punjabi — Punjabi
  'vT0wMbLG5dssaBsksrb6', // Noor — Punjabi
  '1lO8qxpAhRajh9gUaclp', // Guri — Punjabi
  'RgArqterc5zx6RLQqzcs', // Maadhav — Punjabi
  '4QmRQP2RqsuTD7HT9MkW', // Prajakta — Marathi
  'UN3inGyhBayPW0A8lscL', // Kalyani — Marathi
  '8fqGzH80bdkm2UseeV9f', // Sharad — Marathi
  'rXW8MF4nX2gHMvBmCDnr', // Vinayak — Marathi
  'OVkoEbwxsYHiSRMFV9t3', // Samitha — Malayalam
  'AA6iXlJYcLQWoTQnJw8E', // Sunaina — Malayalam
  'F6apuaOciIF8axVr21FB', // Vijayesh — Malayalam
  'SHni3SZjw4bGtj6ec911', // Rinoy — Malayalam
  'UeUC009F3NYPIArcZmq0', // Srivatsa — Kannada
  '1yebI4wPatIbQgkzinlP', // Aisiri — Kannada
  'MYdTqLsWF6IueSvcrO97', // Sangamitra — Kannada
  'nlRBcodAo9LA6ChkhS0i', // Alpesh — Gujarati
  '6dUn0LOxdDTsxwitRcli', // Leelaben — Gujarati
  'YbaN3TxgsspuFPFw8K0E', // Jyoti — Gujarati
  'Jbte7ht1CqapnZvc4KpK', // Kari — Kazakh
  '5dORE7Khzn5FlSDUvuHn', // Guldana — Kazakh
  'inSavBKUniNDYIfIcSyG', // Jaya — Kazakh
  'uQPOhlzA94sogqmhGLCI', // Ilkin — Azerbaijani
  'AKGWkp89mKHLHYRQrjRX', // Azar — Azerbaijani
  'IT5cb4lfodSX8eyXUzyO', // Charles — Afrikaans
  'Kbmk5ZFvBwyoQIcE8k3j', // Marcel — Afrikaans
  'oIOmkwCWrZas3A06ucag', // Annelise — Afrikaans
  'u9JMHD7JqMJ7AOtFgcLs', // Ryan — Afrikaans
  'klHXweKCxxmBYweAPtk4', // Noa — Hebrew (v3 native preview)
  'Wyme86I3aqjVzqRseQFt', // Avi — Hebrew (v3 native preview)
  'Lnkp9jPRNZZmJemTuDD2', // Tamar — Hebrew (v3 native preview)
  '49GHemjSs7Dp4fi77m6O', // Malee — Thai (v3 native preview)
  'yEmQgQOSdHnAHvuvGREc', // Somchai — Thai (v3 native preview)
  'U56LoJ6pU3UIhx4Dipn5', // Anong — Thai (v3 native preview)
  'oKPWAboM53aP6cy4suF7', // Ani — Armenian (v3 native preview)
  'jJxw1Rvgr2c60UdJHPBn', // Aram — Armenian (v3 native preview)
  'oT5lntmfa8t37GiBBd3k', // Lilit — Armenian (v3 native preview)
  'eeJeJFwOg4YUm91SPtjt', // Nino — Georgian (v3 native preview)
  'ZqzABHxB2wSRrgl5cAi6', // Giorgi — Georgian (v3 native preview)
  'ZPImQHFNyEPRDeECS4MX', // Ketevan — Georgian (v3 native preview)
  'U05hO0X5Y4TnTclUk0k7', // Sita — Nepali (v3 native preview)
  'as3rPQuJO7zbm6EL5msD', // Arjun — Nepali (v3 native preview)
  'jPKMTc2yXKdaU88kv5UN', // Maya — Nepali (v3 native preview)
  'VsVIOTkd9zjLUVaQO9TA', // Baraka — Swahili (v3 native preview)
  'VaukKNJWSXwomRsgQyK8', // Zawadi — Swahili (v3 native preview)
  'IwvKQ5U1T8fo4Tj8CVZm', // Amani — Swahili (v3 native preview)
  'poylg979ASDig8oTbLuu', // Ayaan — Somali (v3 native preview)
  'jN2L0WufWIAXnexEWTfh', // Abdi — Somali (v3 native preview)
  '4v4Aiul4nRXeh3c3syXm', // Hodan — Somali (v3 native preview)
  'VyyyOgRmsqOzaZXnKWnI', // Saoirse — Gaelic (v3 native preview)
  'PGWFVTyGJR74i65Fh7SP', // Cillian — Gaelic (v3 native preview)
  'kZKm3BYbXfahOLAxYZG0', // Zarghuna — Pashto (v3 native preview)
  'zhza6dIY7yb1xz5MKTvQ', // Khalid — Pashto (v3 native preview)
  '6t6kdi8hAnBYTTvzE62o', // Kadri — Estonian (v3 native preview)
  'PSvOrlGF6tLJOeiEwkn8', // Marten — Estonian (v3 native preview)
  'XescS24CsOySAkKrLAO3', // Rūta — Lithuanian (v3 native preview)
  'SOs9l9f66jWuKsXJkMBX', // Mantas — Lithuanian (v3 native preview)
  '6Xq32rHt6oAu98TBDKfr', // Maja — Slovenian (v3 native preview)
  'jP6rT0U10vfyLbGW2Wm8', // Luka — Slovenian (v3 native preview)
  '3Drdg7QWqr45nZmYpXRP', // Freyja — Icelandic (v3 native preview)
  'nwuvEDGFwpyogO4zlBHp', // Bjarni — Icelandic (v3 native preview)
  'XtxuRNfvtd45OyWcp5jr', // Núria — Catalan (v3 native preview)
  'q1JEeAYiLAb1FqnPGttA', // Pau — Catalan (v3 native preview)
  's6W2NupNY6TykGJoDtWy', // Alesia — Belarusian (v3 native preview)
  'zKTOd8cxZlIf5EKC5Giv', // Viktar — Belarusian (v3 native preview)
  'CQAD6iKxS73fEAGjSwt5', // Līga — Latvian (v3 native preview)
  'ZsjEJaLQy3sgvwxicmDx', // Kārlis — Latvian (v3 native preview)
  'I3u6waC588j43py1kDN2', // Elena — Macedonian (v3 native preview)
  'dSByRdUbTGloB7TFA1qD', // Stefan — Macedonian (v3 native preview)
  'NEjemlRxgWmL5ZGJetsB', // Maricel — Cebuano (v3 native preview)
  'GmxTc9oJaW27NFnQFhtv', // Dodong — Cebuano (v3 native preview)
  'x5O3bgPT2grYhry6qmWR', // Sri — Javanese (v3 native preview)
  'N7ZPtMSz30kJ869b58WE', // Bagus — Javanese (v3 native preview)
  'h60rOzgfLmYsntfqgGu2', // Aigul — Kyrgyz (v3 native preview)
  'TpZlRcB7rTBboAYWa2DC', // Bakyt — Kyrgyz (v3 native preview)
  'cbqdgvVi3C6sgxIWpqIh', // Esther — Lingala (v3 native preview)
  '78U3k8D8W1rkhXUE5SDH', // Didier — Lingala (v3 native preview)
  'qbfQuoRJv1T3ei3qV4bc', // Josiane — Luxembourgish (v3 native preview)
  'zfWx5IjUaWGWTId9hv9t', // Pit — Luxembourgish (v3 native preview)
  'bgvW7tChsyTpexUZy4KR', // Uxía — Galician (v3 native preview)
  'hLygPNd2gK6Azddorc5W', // Brais — Galician (v3 native preview)
  'e6qsVnCuD0MWxmhZcuKz', // Thandiwe — Chichewa (v3 native preview)
  'bBG9wwa23659EgIkMbc1', // Mphatso — Chichewa (v3 native preview)
  'Op72Fm6dzjYuyMTp6FBl', // Jonaki — Assamese (v3 native preview)
  'wQjKP9DkO6pDLKdaqFn6', // Ankur — Assamese (v3 native preview)
  '3cuC1hNj9E2jcHlIvndN', // Marvi — Sindhi (v3 native preview)
  'sR8sxaLJeFSh308Gi6HS', // Sachal — Sindhi (v3 native preview)
  'KwIgPiOpugJRCqPjgUbK', // Eitan — Hebrew (v3 native preview)
  '06llJPvI62CjqSWOX9Gp', // Shira — Hebrew (v3 native preview)
  'loY1uopAz31XyhAEhNSa', // Niran — Thai (v3 native preview)
  'i6ke7jvmGEVUyV4zjSaT', // Kanya — Thai (v3 native preview)
  'fVuuCHMDnFUgutKVLPo1', // Tigran — Armenian (v3 native preview)
  'OZxMHsGaBmV5pjMIDIn0', // Narine — Armenian (v3 native preview)
  'Y921BUrS17GL37pctIb8', // Levan — Georgian (v3 native preview)
  'fydPJq00SigRIaPxWgiS', // Salome — Georgian (v3 native preview)
  '5Tt3smTZZdAF8jVbfVlP', // Bikash — Nepali (v3 native preview)
  'Nx7FcZgjGWVrAsCg0Csy', // Anisha — Nepali (v3 native preview)
  'MuTiG4dbrEGYEy3XP4iP', // Neema — Swahili (v3 native preview)
  'dtb2DcRUiZX01F9bvBi0', // Juma — Swahili (v3 native preview)
  'C9v09R5AIM6tOX6Fn06I', // Yusuf — Somali (v3 native preview)
  'JVcAsJvkeZVQhXpDP6Ji', // Fartun — Somali (v3 native preview)
  'hg1icMxI2KADq9a81ecq', // Nurlan — Kazakh (v3 native preview)
  'fnyqY1A0BcjAtRpVEd7Z', // Aslan — Kazakh (v3 native preview)
  'QgfJwCRSjPjNs6RJeblX', // Manju — Kannada (v3 native preview)
  '4Q02te4SdfFsVbcIKmbk', // Deepa — Kannada (v3 native preview)
  'C9mPSTSfiDpIm4wTghYI', // Chirag — Gujarati (v3 native preview)
  'CXoGcuszI2UkuF6sqV8W', // Hetal — Gujarati (v3 native preview)
  'dSEhEXLzhnZEytnJ2rRy', // Elmarie — Afrikaans (v3 native preview)
  'XlDdozLmuTofIxK4BjPD', // Aysel — Azerbaijani (v3 native preview)
  'kdkz0RPvRYjuZ1OqDPeY', // Leyla — Azerbaijani (v3 native preview)
  'Tjm71aOgsbTK9LM4TiSa', // Rashad — Azerbaijani (v3 native preview)
  'ObcUVfo3RbQv9i2RBbAe', // Aoife — Gaelic (v3 native preview)
  'ZDsEGXYAf6c4QY0LNSHr', // Padraig — Gaelic (v3 native preview)
  'q1eMiEpfmoW18ppbTyct', // Niamh — Gaelic (v3 native preview)
  '5UFzavnH3OCXmnkfTBPr', // Gulnaz — Pashto (v3 native preview)
  'mxbgw5PwaQHOrln90mhH', // Rahim — Pashto (v3 native preview)
  'X4ISDuZaYvJHnjm8ylvJ', // Torpekai — Pashto (v3 native preview)
  'Bsa1HP3wF8JGRzGouFEa', // Liina — Estonian (v3 native preview)
  'tEo3d4j7gzVojBL5Z4Pt', // Tanel — Estonian (v3 native preview)
  'PBjLN8WJmwHEGB1u1UzI', // Piret — Estonian (v3 native preview)
  'R24r0IV80Iw9eyByq1ps', // Egle — Lithuanian (v3 native preview)
  'gP52hjM48hyNoVCbizxO', // Dovydas — Lithuanian (v3 native preview)
  'Kfn3fYy8XcPyUvbTxn1H', // Vilma — Lithuanian (v3 native preview)
  'ET6E1GmpkG4a6Sotpwm3', // Nika — Slovenian (v3 native preview)
  'vvBjyHnGMjZCXQ86cBYy', // Ziga — Slovenian (v3 native preview)
  'dCfjdikb3Vzuo3qxhmwc', // Tjasa — Slovenian (v3 native preview)
  'GQrVSQduiFCVTMHGavV5', // Sigrun — Icelandic (v3 native preview)
  '8oLiG8m8nM6NUEzIvzhz', // Gunnar — Icelandic (v3 native preview)
  'xYqBp1MrrrBpxIdqTvgb', // Katla — Icelandic (v3 native preview)
  '6LXO12QQcaZd6xGp9UIZ', // Laia — Catalan (v3 native preview)
  'VynqN8qbEfEZNZlVOik7', // Jordi — Catalan (v3 native preview)
  'J2cEQqXvE6V7TyyxnTvL', // Mireia — Catalan (v3 native preview)
  'kQ2VDreF7XEea8InkZmA', // Hanna — Belarusian (v3 native preview)
  'tldkj5HGRWTtMBjOYqli', // Maksim — Belarusian (v3 native preview)
  'JcWDFG8DiES2OzGhZJUJ', // Volha — Belarusian (v3 native preview)
  'aMmeBf0lzDYlouyfqNjh', // Ilze — Latvian (v3 native preview)
  'pXzlRykxH388Ltp35fHG', // Janis — Latvian (v3 native preview)
  'RGI3cldvKRqgPfGMoCLC', // Dace — Latvian (v3 native preview)
  'uC9VI5XrTxXRNlCzGSKR', // Biljana — Macedonian (v3 native preview)
  '49dczJmfGX7RoeudoOPi', // Nikola — Macedonian (v3 native preview)
  '6l0ObIy4mHn0XfeKlCgW', // Marija — Macedonian (v3 native preview)
  'uQiZyJqZ7ePaB8bwME8n', // Inday — Cebuano (v3 native preview)
  'Fjiik8aKXNDjmxd1Q2jF', // Junrey — Cebuano (v3 native preview)
  'uG1JFy6xppqckhHCs2KG', // Liza — Cebuano (v3 native preview)
  'LI283TzJcIwSKqsXiH7u', // Rini — Javanese (v3 native preview)
  'ri2GaG9XLBC34ZByBU1U', // Slamet — Javanese (v3 native preview)
  'WqfqZq3UXPCD87IXHLnm', // Wulan — Javanese (v3 native preview)
  'bjoUrk7s2fY9cu2u67KF', // Nazira — Kyrgyz (v3 native preview)
  'NJGktYrreZwyjxhPOpIz', // Talant — Kyrgyz (v3 native preview)
  'ZRJMGKt2Okf3o9C38eSq', // Cholpon — Kyrgyz (v3 native preview)
  'eptbh5KQei9tCWyAUN37', // Grace — Lingala (v3 native preview)
  'GxEkXZFVTiRn1HdPNqar', // Patrice — Lingala (v3 native preview)
  'nHIe7B3HzjldgtM8jZ3S', // Solange — Lingala (v3 native preview)
  'tgkNj6krX4qIUNYOcEVg', // Martine — Luxembourgish (v3 native preview)
  'gZR4sGJmJaoBOVzKgZ8j', // Claude — Luxembourgish (v3 native preview)
  '5RrtCIBG7tAdY1hLObUP', // Lynn — Luxembourgish (v3 native preview)
  'z8KYiNHfPR756XyDo3ZY', // Antia — Galician (v3 native preview)
  'ZgMby7YWr4TYCWb4rGea', // Xoan — Galician (v3 native preview)
  'wrkWX7KvbDI4i5bckQST', // Sabela — Galician (v3 native preview)
  'ldzFbnOs5w23MLcNBXQx', // Chisomo — Chichewa (v3 native preview)
  '2GJZCZIWrWiGFDntCFaz', // Kondwani — Chichewa (v3 native preview)
  'S3RXi1qdUT4is6DxRHdi', // Mercy — Chichewa (v3 native preview)
  'imphBib61OiJ8r9IfSGe', // Pori — Assamese (v3 native preview)
  'BFRai5zo6IWomwFrcjIb', // Bhaskar — Assamese (v3 native preview)
  'C8uRRxxNZH0vRqJbVFJy', // Rima — Assamese (v3 native preview)
  'ZiK4vToL7fv1vROW8pbA', // Sassui — Sindhi (v3 native preview)
  '0xHziZolI8Tp6rLtUqh2', // Ayaz — Sindhi (v3 native preview)
  'm221hZUK6hT6r9TTV3R9', // Zeenat — Sindhi (v3 native preview)
  'DAGnQ7r9sMtV0Q44g1Mi', // Nikola — Serbian (v3)
  'BognUUMX6W1qmZKB2TOw', // Adrian — Persian (Farsi) (v3)
  'AiJXmyKRr43pvJUsR0tP', // Roya — Persian (Farsi) (v3)
  '1DVmAL9GdV3a2eJEV0rS', // Amina — Bosnian (v3)
  'dfsWmmgleAAdssZ0Spld', // Emir — Bosnian (v3)
  'o85TqPN3F4P7dWae2paA', // Meher — Urdu (v3)
  'QhHNJKW7n5VCJm12Xq1F', // Danyal — Urdu (v3)
  'u3v81nA6jgD2f8PNeClc', // Khabir — Bengali (v3)
  'iuABfyf7pRoBzuPqzUCt', // Binod — Bengali (v3)
  'f4UZfBDD7d4QR1DcXAMf', // Sai Datta — Telugu (v3)
  'libO4zik1vSTqRw18D3i', // Nishant — Punjabi (v3)
  'WoB1yCV3pS7cFlDlu8ZU', // Krishna — Punjabi (v3)
  'RBxPIvrKOP4ugCK2jVHD', // Setu — Marathi (v3)
  'ritE7mWIE55SDe0J11Kk', // Vardaan — Marathi (v3)
  'pHv3gTvpbdvT6nHZlDhw', // Raju — Malayalam (v3)
  'Mz4GsCKSPU4BZl2sMASU', // Sanjayan — Malayalam (v3)
]);

export const isV3Voice = (voiceId?: string | null): boolean =>
  !!voiceId && V3_VOICE_IDS.has(voiceId);
